/**
 * AI Usage History Page
 *
 * Comprehensive AI usage analytics dashboard showing:
 * - Detailed metrics and statistics
 * - Usage trend charts
 * - Paginated history table with filters
 * - Cost tracking and optimization insights
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  ArrowLeft,
  TrendingUp,
  Activity,
  Zap,
  DollarSign,
  CheckCircle,
  XCircle,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  getAIUsageDashboard,
  getAIUsageHistory,
  getAIUsageTimeline,
  formatCost,
  formatTokens,
  formatNumber,
  formatResponseTime
} from '@/services/api/aiUsageApi';
import { AIRequestType, AIProvider } from '@/types/aiUsage';

export function AIUsageHistoryPage() {
  const navigate = useNavigate();

  // State for filters and pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [requestTypeFilter, setRequestTypeFilter] = useState<string>('all');
  const [successFilter, setSuccessFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [timeframe, setTimeframe] = useState<'day' | 'week' | 'month'>('week');

  // Fetch dashboard summary
  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['ai-usage-dashboard'],
    queryFn: getAIUsageDashboard,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Fetch timeline for charts
  const { data: timelineData, isLoading: isTimelineLoading } = useQuery({
    queryKey: ['ai-usage-timeline', timeframe],
    queryFn: () => getAIUsageTimeline({
      timeframe,
      days: timeframe === 'day' ? 7 : timeframe === 'week' ? 30 : 90
    }),
    staleTime: 1000 * 60 * 2,
  });

  // Fetch history with filters
  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['ai-usage-history', page, limit, requestTypeFilter, successFilter, providerFilter],
    queryFn: () => getAIUsageHistory({
      page,
      limit,
      requestType: requestTypeFilter !== 'all' ? requestTypeFilter : undefined,
      success: successFilter !== 'all' ? successFilter === 'true' : undefined,
      provider: providerFilter !== 'all' ? providerFilter : undefined
    }),
    staleTime: 1000 * 60 * 2,
  });

  const dashboard = dashboardData?.data;
  const timeline = timelineData?.data || [];
  const history = historyData;

  // Prepare chart data
  const chartData = timeline
    .reduce((acc: any[], point) => {
      const existing = acc.find(item => item.date === point.date);
      if (existing) {
        existing.requests += point.requests;
        existing.tokens += point.tokens;
        existing.cost += point.cost;
      } else {
        acc.push({
          date: point.date,
          requests: point.requests,
          tokens: point.tokens,
          cost: point.cost
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.date.localeCompare(b.date));

  // Export to CSV handler
  const handleExportCSV = () => {
    if (!history?.data) return;

    const headers = ['Timestamp', 'Type', 'Provider', 'Model', 'Success', 'Tokens', 'Cost', 'Response Time', 'Cached'];
    const rows = history.data.map(record => [
      new Date(record.timestamp).toISOString(),
      record.requestType,
      record.provider,
      record.aiModel,
      record.success ? 'Yes' : 'No',
      record.tokens.total,
      record.cost.toFixed(4),
      `${record.responseTime}ms`,
      record.cached ? 'Yes' : 'No'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-usage-${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">AI Usage Analytics</h1>
            <p className="text-muted-foreground">Detailed insights into your AI generation activity</p>
          </div>
        </div>
        <Button onClick={handleExportCSV} disabled={!history?.data || history.data.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Metrics */}
      {isDashboardLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : dashboard ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Generations</p>
                  <p className="text-3xl font-bold">{formatNumber(dashboard.summary.totalGenerations)}</p>
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </div>
                <Activity className="h-10 w-10 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                  <p className="text-3xl font-bold">{dashboard.summary.last7Days.successRate.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tokens</p>
                  <p className="text-3xl font-bold">{formatTokens(dashboard.summary.last7Days.tokensUsed)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
                </div>
                <Zap className="h-10 w-10 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cost</p>
                  <p className="text-3xl font-bold">{formatCost(dashboard.summary.last30Days.totalCost)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                </div>
                <DollarSign className="h-10 w-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Trend Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Usage Trend</CardTitle>
                <CardDescription>AI generations over time</CardDescription>
              </div>
              <Select value={timeframe} onValueChange={(value: any) => setTimeframe(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isTimelineLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="requests"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    name="Generations"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cost & Tokens Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Tokens & Cost</CardTitle>
            <CardDescription>Resource consumption over time</CardDescription>
          </CardHeader>
          <CardContent>
            {isTimelineLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    formatter={(value: number, name: string) => {
                      if (name === 'Cost') return formatCost(value);
                      if (name === 'Tokens') return formatTokens(value);
                      return value;
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="tokens" fill="#3b82f6" name="Tokens" />
                  <Bar yAxisId="right" dataKey="cost" fill="#10b981" name="Cost" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Usage History</CardTitle>
              <CardDescription>Detailed record of AI requests</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <Label htmlFor="request-type" className="text-xs">Request Type</Label>
              <Select value={requestTypeFilter} onValueChange={setRequestTypeFilter}>
                <SelectTrigger id="request-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.values(AIRequestType).map(type => (
                    <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="success" className="text-xs">Status</Label>
              <Select value={successFilter} onValueChange={setSuccessFilter}>
                <SelectTrigger id="success">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Successful</SelectItem>
                  <SelectItem value="false">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="provider" className="text-xs">Provider</Label>
              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger id="provider">
                  <SelectValue placeholder="All providers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  {Object.values(AIProvider).map(provider => (
                    <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isHistoryLoading ? (
            <Skeleton className="h-96" />
          ) : history?.data && history.data.length > 0 ? (
            <>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Response Time</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.data.map((record) => (
                      <TableRow key={record._id}>
                        <TableCell className="text-sm">
                          {new Date(record.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {record.requestType.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{record.provider}</span>
                          {record.cached && (
                            <Badge variant="secondary" className="ml-2 text-xs">Cached</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatTokens(record.tokens.total)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {formatCost(record.cost)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatResponseTime(record.responseTime)}
                        </TableCell>
                        <TableCell className="text-center">
                          {record.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600 inline" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600 inline" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {history.meta.pagination && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((history.meta.pagination.page - 1) * history.meta.pagination.limit) + 1} to{' '}
                    {Math.min(history.meta.pagination.page * history.meta.pagination.limit, history.meta.pagination.totalRecords)}{' '}
                    of {history.meta.pagination.totalRecords} results
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={!history.meta.pagination.hasPreviousPage}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={!history.meta.pagination.hasNextPage}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No usage history found</p>
              <p className="text-sm">Try adjusting your filters or generate some pages with AI</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
